import {
  WebsiteSchema,
  OrganizationSchema,
  BreadcrumbSchema,
  PageSchema,
  ServiceSchema,
  generateWebsiteSchema,
  generateOrganizationSchema,
  generateBreadcrumbSchema,
  generatePageSchema,
  generateServiceSchema,
  combineSchemas
} from './schemaMarkup';

// Define the organization details (to be used across pages)
const organization: OrganizationSchema = {
  name: "CTRL P",
  url: "https://ctrlp.co.in",
  logo: "https://ctrlp.co.in/logo192.png",
  description: "CTRL P is a SaaS based printing service that allows users to upload documents, customize print settings, and send documents for printing at print shops near them.",
  contactPoint: {
    telephone: "+917770927092",
    contactType: "customer service",
    email: "info@ctrlp.co.in"
  }
};

// Define the website details
const website: WebsiteSchema = {
  name: "CTRL P",
  url: "https://ctrlp.co.in",
  logo: "https://ctrlp.co.in/logo192.png"
};

// Generate schema for Home/Landing page
export const generateHomePageSchema = (): string => {
  const page: PageSchema = {
    title: "CTRL P - Remote Document Printing Solution",
    description: "CTRL P is a fast and efficient document printing solution for students and businesses to print documents remotely. Upload, select print settings, send documents for printing and collect at your convenience.",
    url: "https://ctrlp.co.in",
    dateModified: new Date().toISOString()
  };

  // Services offered
  const printingService: ServiceSchema = {
    name: "Document Printing Services",
    description: "Upload, select print settings, and print your documents at nearby shops.",
    provider: {
      name: "CTRL P",
      url: "https://ctrlp.co.in"
    },
    serviceType: "Printing Service",
    areaServed: "India"
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const websiteSchema = generateWebsiteSchema(website);
  const pageSchema = generatePageSchema(page);
  const serviceSchema = generateServiceSchema(printingService);

  // Combine schemas
  return combineSchemas([organizationSchema, websiteSchema, pageSchema, serviceSchema]);
};

// Generate schema for Get Print / Upload page
export const generateGetPrintPageSchema = (): string => {
  const page: PageSchema = {
    title: "Upload & Print Documents | CTRL P",
    description: "Upload your documents, customize print settings and choose a convenient shop for sending documents. Print in black & white or color with various paper options.",
    url: "https://ctrlp.co.in/get-print",
    dateModified: new Date().toISOString()
  };

  // Service schema
  const printService: ServiceSchema = {
    name: "Document Printing Service",
    description: "Upload your documents, set printing preferences, and pick up at a nearby location.",
    provider: {
      name: "CTRL P",
      url: "https://ctrlp.co.in"
    },
    serviceType: "Printing Service",
    offers: {
      price: 1,
      priceCurrency: "INR"
    }
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Get Print",
        item: "https://ctrlp.co.in/get-print"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const serviceSchema = generateServiceSchema(printService);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, serviceSchema, breadcrumbSchema]);
};

// Generate schema for Print Bazaar page
export const generatePrintBazaarPageSchema = (): string => {
  const page: PageSchema = {
    title: "Print Bazaar - Document Marketplace | CTRL P",
    description: "Browse and purchase pre-uploaded documents, academic materials, notes, and more from our Print Bazaar marketplace.",
    url: "https://ctrlp.co.in/print-bazaar",
    dateModified: new Date().toISOString()
  };

  // Service schema
  const marketplaceService: ServiceSchema = {
    name: "Print Bazaar Document Marketplace",
    description: "A pre-loaded collection of notes and study materials available directly in the system for printing.",
    provider: {
      name: "CTRL P",
      url: "https://ctrlp.co.in"
    },
    serviceType: "Marketplace"
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Print Bazaar",
        item: "https://ctrlp.co.in/print-bazaar"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const serviceSchema = generateServiceSchema(marketplaceService);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, serviceSchema, breadcrumbSchema]);
};

// Generate schema for Shop Registration page
export const generateShopRegistrationPageSchema = (): string => {
  const page: PageSchema = {
    title: "Shop Registration - Partner with CTRL P",
    description: "Register your print shop with CTRL P and start receiving print orders from customers in your area.",
    url: "https://ctrlp.co.in/shop-registration",
    dateModified: new Date().toISOString()
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Shop Registration",
        item: "https://ctrlp.co.in/shop-registration"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, breadcrumbSchema]);
};

// Generate schema for Advertising page
export const generateAdvertisingPageSchema = (): string => {
  const page: PageSchema = {
    title: "Advertising - Promote Your Brand or Product with CTRL P",
    description: "Place your brand on document coverpages seen by thousands of students daily. Premium ad space that gets noticed at the moment of highest engagement.",
    url: "https://ctrlp.co.in/ads",
    dateModified: new Date().toISOString()
  };

  // Service schema
  const advertisingService: ServiceSchema = {
    name: "Document Coverpage Advertising",
    description: "Place your brand on document coverpages seen by thousands of students daily.",
    provider: {
      name: "CTRL P",
      url: "https://ctrlp.co.in"
    },
    serviceType: "Advertising Service"
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Advertising",
        item: "https://ctrlp.co.in/ads"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const serviceSchema = generateServiceSchema(advertisingService);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, serviceSchema, breadcrumbSchema]);
};

// Generate schema for Spiral Binding page
export const generateSpiralBindingPageSchema = (): string => {
  const page: PageSchema = {
    title: "Spiral Binding Services | CTRL P",
    description: "Get your documents professionally spiral bound for presentations, reports, and academic work. Upload your document and submit spiral binding requests.",
    url: "https://ctrlp.co.in/spiral-binding",
    dateModified: new Date().toISOString()
  };

  // Service schema
  const bindingService: ServiceSchema = {
    name: "Spiral Binding Service",
    description: "Professional spiral binding for documents, presentations, reports, and academic work.",
    provider: {
      name: "CTRL P",
      url: "https://ctrlp.co.in"
    },
    serviceType: "Binding Service",
    offers: {
      price: 30,
      priceCurrency: "INR"
    }
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Spiral Binding",
        item: "https://ctrlp.co.in/spiral-binding"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const serviceSchema = generateServiceSchema(bindingService);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, serviceSchema, breadcrumbSchema]);
};

// Generate schema for Terms and Conditions page
export const generateTermsPageSchema = (): string => {
  const page: PageSchema = {
    title: "Terms and Conditions | CTRL P",
    description: "Read the terms and conditions governing the use of CTRL P services, including document printing, Print Bazaar, and more.",
    url: "https://ctrlp.co.in/terms-and-conditions",
    dateModified: "2025-01-30T00:00:00+05:30"  // Last updated date from the page
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Terms and Conditions",
        item: "https://ctrlp.co.in/terms-and-conditions"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, breadcrumbSchema]);
};

// Generate schema for Privacy Policy page
export const generatePrivacyPolicyPageSchema = (): string => {
  const page: PageSchema = {
    title: "Privacy Policy | CTRL P",
    description: "Learn about how CTRL P collects, uses, and protects your personal information and privacy rights.",
    url: "https://ctrlp.co.in/privacy-policy",
    dateModified: "2025-01-30T00:00:00+05:30"  // Assuming same update date as Terms
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Privacy Policy",
        item: "https://ctrlp.co.in/privacy-policy"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, breadcrumbSchema]);
};

// Generate schema for Sitemap page
export const generateSitemapPageSchema = (): string => {
  const page: PageSchema = {
    title: "Sitemap | CTRL P",
    description: "Navigate through all pages and services available on the CTRL P website.",
    url: "https://ctrlp.co.in/sitemap",
    dateModified: new Date().toISOString()
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Sitemap",
        item: "https://ctrlp.co.in/sitemap"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, breadcrumbSchema]);
};

// Generate schema for My Account page
export const generateMyAccountPageSchema = (): string => {
  // This is a private page, so we'll keep it simple
  const page: PageSchema = {
    title: "My Account | CTRL P",
    description: "Manage your CTRL P account, view your orders, print bazaar uploads and wallet transactions.",
    url: "https://ctrlp.co.in/my-account",
    dateModified: new Date().toISOString()
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "My Account",
        item: "https://ctrlp.co.in/my-account"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, breadcrumbSchema]);
};

// Generate schema for Nagpur page
export const generateNagpurPageSchema = (): string => {
  const page: PageSchema = {
    title: "CTRL+P in Nagpur | Printing Services",
    description: "CTRL+P printing services in Nagpur. Find our locations across colleges and institutions in Nagpur.",
    url: "https://ctrlp.co.in/cities/nagpur",
    image: "https://ctrlp.co.in/assets/images/city-banners/nagpur.png",
    dateModified: new Date().toISOString()
  };

  // Service schema
  const printService: ServiceSchema = {
    name: "Document Printing Service in Nagpur",
    description: "Fast, reliable, and convenient printing services across the Orange City. Available at major educational institutions including IIIT, MNLU, YCCE, AIIMS, IIM, and G.H. Raisoni College.",
    provider: {
      name: "CTRL P",
      url: "https://ctrlp.co.in"
    },
    serviceType: "Printing Service",
    areaServed: "Nagpur, Maharashtra"
  };

  // Breadcrumb
  const breadcrumb: BreadcrumbSchema = {
    items: [
      {
        name: "Home",
        item: "https://ctrlp.co.in"
      },
      {
        name: "Cities",
        item: "https://ctrlp.co.in/cities"
      },
      {
        name: "Nagpur",
        item: "https://ctrlp.co.in/cities/nagpur"
      }
    ]
  };

  // Generate individual schemas
  const organizationSchema = generateOrganizationSchema(organization);
  const pageSchema = generatePageSchema(page);
  const serviceSchema = generateServiceSchema(printService);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  // Combine schemas
  return combineSchemas([organizationSchema, pageSchema, serviceSchema, breadcrumbSchema]);
};

// Function to get schema based on current route
export const getSchemaMarkupForRoute = (route: string): string => {
  // Normalize the route
  const normalizedRoute = route.endsWith('/') ? route.slice(0, -1) : route;
  
  // Match route to schema generator
  switch (normalizedRoute) {
    case '':
    case '/':
      return generateHomePageSchema();
    case '/get-print':
      return generateGetPrintPageSchema();
    case '/print-bazaar':
      return generatePrintBazaarPageSchema();
    case '/shop-registration':
      return generateShopRegistrationPageSchema();
    case '/ads':
      return generateAdvertisingPageSchema();
    case '/spiral-binding':
      return generateSpiralBindingPageSchema();
    case '/terms-and-conditions':
      return generateTermsPageSchema();
    case '/privacy-policy':
      return generatePrivacyPolicyPageSchema();
    case '/sitemap':
      return generateSitemapPageSchema();
    case '/my-account':
      return generateMyAccountPageSchema();
    case '/cities/nagpur':
      return generateNagpurPageSchema();
    default:
      // For unknown routes, return basic organization and website schema
      return combineSchemas([
        generateOrganizationSchema(organization),
        generateWebsiteSchema(website)
      ]);
  }
};
